/**
 * Enhanced SSE Connection Manager with graceful shutdown support
 */

export class SSEConnectionManager {
    constructor(userId, sessionId, onMessage, onStatusChange) {
        this.userId = userId;
        this.sessionId = sessionId;
        this.onMessage = onMessage;
        this.onStatusChange = onStatusChange;
        this.connection = null;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 10;
        this.reconnectDelay = 1000; // Start with 1 second
        this.maxReconnectDelay = 30000; // Max 30 seconds
        this.isShuttingDown = false;
        this.heartbeatInterval = null;
        this.lastHeartbeat = null;
    }

    connect() {
        if (!this.userId || !this.sessionId) {
            console.warn('[SSE] Cannot connect without userId and sessionId');
            return;
        }

        this.close(); // Close any existing connection

        try {
            const url = `/api/session-status?sessionId=${encodeURIComponent(this.sessionId)}`;
            this.connection = new EventSource(url);
            
            this.connection.onopen = () => {
                console.log('[SSE] Connection established');
                this.reconnectAttempts = 0;
                this.reconnectDelay = 1000;
                this.onStatusChange?.('connected');
                this.startHeartbeatMonitor();
            };

            this.connection.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    
                    // Handle server shutdown notification
                    if (data.event === 'server_shutdown') {
                        console.log('[SSE] Server shutdown notification received');
                        this.isShuttingDown = true;
                        this.onStatusChange?.('server_restarting', data.data?.message);
                        // Don't attempt reconnection immediately
                        this.scheduleReconnect(5000); // Wait 5 seconds before trying
                        return;
                    }

                    // Handle heartbeat
                    if (data.type === 'heartbeat') {
                        this.lastHeartbeat = Date.now();
                        return;
                    }

                    // Pass other messages to handler
                    this.onMessage?.(data);
                } catch (error) {
                    console.error('[SSE] Error parsing message:', error);
                }
            };

            this.connection.onerror = (error) => {
                console.error('[SSE] Connection error:', error);
                this.stopHeartbeatMonitor();
                
                if (this.connection?.readyState === EventSource.CLOSED) {
                    this.onStatusChange?.('disconnected');
                    this.scheduleReconnect();
                }
            };

        } catch (error) {
            console.error('[SSE] Failed to create connection:', error);
            this.onStatusChange?.('error', error.message);
        }
    }

    startHeartbeatMonitor() {
        this.stopHeartbeatMonitor();
        this.lastHeartbeat = Date.now();
        
        // Check heartbeat every 45 seconds (expecting heartbeat every 30 seconds)
        this.heartbeatInterval = setInterval(() => {
            const timeSinceLastHeartbeat = Date.now() - this.lastHeartbeat;
            if (timeSinceLastHeartbeat > 45000) {
                console.warn('[SSE] Heartbeat timeout, reconnecting...');
                this.onStatusChange?.('heartbeat_timeout');
                this.reconnect();
            }
        }, 45000);
    }

    stopHeartbeatMonitor() {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
    }

    scheduleReconnect(customDelay = null) {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error('[SSE] Max reconnection attempts reached');
            this.onStatusChange?.('max_retries_exceeded');
            return;
        }

        const delay = customDelay || this.getReconnectDelay();
        console.log(`[SSE] Scheduling reconnect in ${delay}ms (attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`);
        
        this.onStatusChange?.('reconnecting', { 
            attempt: this.reconnectAttempts + 1, 
            maxAttempts: this.maxReconnectAttempts,
            delay 
        });

        setTimeout(() => {
            if (this.userId && this.sessionId) {
                this.reconnectAttempts++;
                this.connect();
            }
        }, delay);
    }

    getReconnectDelay() {
        // Exponential backoff with jitter
        const baseDelay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts), this.maxReconnectDelay);
        const jitter = Math.random() * 0.3 * baseDelay; // 30% jitter
        return Math.floor(baseDelay + jitter);
    }

    reconnect() {
        this.close();
        this.connect();
    }

    close() {
        this.stopHeartbeatMonitor();
        if (this.connection) {
            this.connection.close();
            this.connection = null;
        }
    }

    updateCredentials(userId, sessionId) {
        const needsReconnect = this.userId !== userId || this.sessionId !== sessionId;
        this.userId = userId;
        this.sessionId = sessionId;
        
        if (needsReconnect && this.connection) {
            this.reconnect();
        }
    }

    resetReconnectAttempts() {
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        this.isShuttingDown = false;
    }
}

// Helper function to show user-friendly connection status
export const getConnectionStatusMessage = (status, data, t) => {
    switch (status) {
        case 'connected':
            return { type: 'success', message: t('sseConnected') || 'Connected to server' };
        case 'disconnected':
            return { type: 'warning', message: t('sseDisconnected') || 'Connection lost' };
        case 'reconnecting':
            return { 
                type: 'info', 
                message: t('sseReconnecting', { 
                    attempt: data?.attempt, 
                    max: data?.maxAttempts 
                }) || `Reconnecting... (${data?.attempt}/${data?.maxAttempts})` 
            };
        case 'server_restarting':
            return { 
                type: 'info', 
                message: data || t('sseServerRestarting') || 'Server is updating. Your session will be restored shortly.' 
            };
        case 'heartbeat_timeout':
            return { type: 'warning', message: t('sseHeartbeatTimeout') || 'Connection timeout, reconnecting...' };
        case 'max_retries_exceeded':
            return { type: 'error', message: t('sseMaxRetriesExceeded') || 'Unable to connect to server' };
        case 'error':
            return { type: 'error', message: data || t('sseError') || 'Connection error' };
        default:
            return null;
    }
};